import React, { useEffect, useState } from 'react'
import { Box, Grid, CircularProgress, Typography } from '@mui/material'
import { TypoGraph, Icon } from 'components'
import { reportException } from 'tools/logs'
import { MilesByMonth } from 'interfaces/ISummaryReport'
import strings from 'strings'
import styles from './estimatedMilesTotal.module.scss'
import { getMilesByMonth } from 'api'
import moment from 'moment'

const t = strings.SUMMARY_REPORT.ESTIMATED_MILES

interface EstimatedMilesByMonth {
  month: string
  estimatedMiles: number | null
  activeUnits: number | null
  avgPerUnit: number | null
}

interface EstimatedMilesData {
  summary: {
    totalMiles: number
    period: string
  }
  breakdownByMonth: EstimatedMilesByMonth[]
}

const EstimatedMiles: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [data, setData] = useState<EstimatedMilesData | null>(null)

  const carrierId = localStorage.getItem('carrierId') || '1'
  const onboardingDate = localStorage.getItem('onboardingDate')
  const endDate = new Date()
  const startDate = new Date()
  startDate.setMonth(startDate.getMonth() - 12)

  const formatDate = (date: Date): string => {
    return date.toISOString().split('T')[0]
  }

  const formatMonthYear = (dateString: string): string => {
    const [year, month] = dateString.split('-')
    const date = new Date(parseInt(year), parseInt(month) - 1)
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  }

  const formatPeriod = (startDate: string, endDate: string): string => {
    const start = new Date(startDate)
    const end = new Date(endDate)
    return `${start.getMonth() + 2}/${start.getFullYear()} - ${
      end.getMonth() + 1
    }/${end.getFullYear()}`
  }

  const transformApiData = (
    apiData: MilesByMonth,
    onboardingDateString: string | null
  ): EstimatedMilesData => {
    const onboardingMoment = onboardingDateString
      ? moment(onboardingDateString)
      : null

    const breakdownByMonth = apiData.breakdownByMonth.map((monthData) => {
      const monthMoment = moment(monthData.date)

      const isBeforeOnboarding =
        onboardingMoment && monthMoment.isBefore(onboardingMoment, 'month')

      return {
        month: formatMonthYear(monthData.date),
        estimatedMiles: isBeforeOnboarding && monthData.totalGpsMiles === 0
          ? null
          : Math.round(monthData.totalGpsMiles),
        activeUnits: isBeforeOnboarding && monthData.totalVehicles === 0 ? null : monthData.totalVehicles,
        avgPerUnit: isBeforeOnboarding && monthData.avgGpsMilesVehicles === 0
          ? null
          : Math.round(monthData.avgGpsMilesVehicles),
      }
    })

    return {
      summary: {
        totalMiles: Math.round(apiData.summary.totalGpsMiles),
        period: formatPeriod(
          apiData.summary.period.startDate,
          apiData.summary.period.endDate
        ),
      },
      breakdownByMonth,
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      if (!carrierId) return

      setIsLoading(true)
      try {
        const apiData = await getMilesByMonth(
          carrierId,
          formatDate(startDate),
          formatDate(endDate)
        )
        const transformedData = transformApiData(apiData, onboardingDate)
        setData(transformedData)
      } catch (err) {
        reportException(err)
        setData(null)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [carrierId])

  const TotalMileage = () => (
    <Grid mb={2}>
      <div className={styles.panelHeader}>
        <div className={styles.panelIcon}>
          <Icon
            materialIcon="TrendingUp"
            boxClassName={styles.trendingIcon}
            color="#3c82f6"
          />
        </div>
        <Typography variant="h3">{t.TOTAL_MILEAGE}</Typography>
      </div>
      <Box className={styles.totalMilesPaper}>
        {isLoading ? (
          <CircularProgress size={40} className={styles.circularProgress} />
        ) : (
          <Box>
            <Typography className={styles.sumEstimatedMiles}>
              {data && data.summary.totalMiles
                ? Math.round(data.summary.totalMiles).toLocaleString()
                : t.NO_ESTIMATED_MILEAGE_DATA}
            </Typography>
            <Typography variant="body1" mb={1}>
              {t.TOTAL_ESTIMATED_MILES_12_MONTHS}
            </Typography>
            <Typography variant="body2" color={'#6b7280'}>
              {data?.summary.period ?? '--'}
            </Typography>
          </Box>
        )}
      </Box>
    </Grid>
  )

  const MonthlyBreakdown = () => (
    <Grid>
      <div className={styles.panelHeader}>
        <div className={styles.panelIcon}>
          <Icon
            materialIcon="TrendingUp"
            boxClassName={styles.trendingIcon}
            color="#3c82f6"
          />
        </div>
        <Typography variant="h3">
          {t.TOTAL_ESTIMATED_MILES_12_MONTHS}
        </Typography>
      </div>

      <Grid container spacing={2}>
        {isLoading ? (
          <Grid item xs={12} className={styles.loadingContainer}>
            <CircularProgress size={30} className={styles.loadingSpinner} />
          </Grid>
        ) : (
          data?.breakdownByMonth.map((month, idx) => (
            <Grid item xs={12} sm={6} md={4} lg={2} key={idx}>
              <Box className={styles.monthlyBreakdownPaper}>
                <Typography variant="h6" className={styles.monthTitle}>
                  {month.month}
                </Typography>

                <Typography variant="h4" className={styles.estimatedMiles}>
                  {month.estimatedMiles !== null
                    ? month.estimatedMiles.toLocaleString()
                    : 'N/A'}
                </Typography>
                <Typography variant="caption" className={styles.label}>
                  {t.ESTIMATED_MILES}
                </Typography>

                <Typography variant="body2" className={styles.dataValue}>
                  {month.activeUnits !== null ? month.activeUnits : 'N/A'}
                </Typography>
                <Typography variant="caption" className={styles.label}>
                  {t.ACTIVE_UNITS}
                </Typography>

                <Typography variant="body2" className={styles.dataValue}>
                  {month.avgPerUnit !== null
                    ? month.avgPerUnit.toLocaleString()
                    : 'N/A'}
                </Typography>
                <Typography variant="caption" className={styles.label}>
                  {t.AVG_PER_UNIT}
                </Typography>
              </Box>
            </Grid>
          ))
        )}
      </Grid>
    </Grid>
  )

  const Footer = () => {
    let formattedDate = ''

    if (onboardingDate) {
      formattedDate = moment(onboardingDate).format('MMMM YYYY')
    }

    const dataAvailabilityNote = t.DATA_AVAILABILITY_NOTE.replace(
      '{DATE}',
      formattedDate
    )

    return (
      <Box className={styles.footerContainer}>
        <Typography variant="caption" className={styles.footerNote}>
          {dataAvailabilityNote}
        </Typography>
        <Typography variant="caption" className={styles.footerNoteLast}>
          {t.MILES_CALCULATION_NOTE}
        </Typography>
      </Box>
    )
  }

  return (
    <Grid mb={3}>
      <TypoGraph variant="h2" mb={2} content={t.ESTIMATED_MILES_TITLE} />
      <TotalMileage />
      <MonthlyBreakdown />
      <Footer />
    </Grid>
  )
}

export default EstimatedMiles
